import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorService } from '../shared/services/error.service';

export interface ServiceOrderLog {
  id: number;
  serviceOrderId: number | null;
  department: string | null;
  userId: number | null;
  userName: string | null;
  action: string | null;
  description: string | null;
  details: string | null;
  oldStatus: string | null;
  newStatus: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  raw?: any;
}

export interface ServiceOrderLogFilters {
  pageNumber?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
  department?: string;
  serviceOrderId?: number;
}

export interface PagedServiceOrderLogs {
  items: ServiceOrderLog[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ServiceOrderLogsService {
  private readonly baseUrl = environment.microservices?.order || environment.apiUrl || '/api';
  private readonly resourcePath = 'serviceorderlogs';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  private buildUrl(path: string): string {
    const [base, baseQuery] = String(this.baseUrl).split('?');
    const normalizedBase = base.replace(/\/+$/, '');
    const normalizedPath = String(path).replace(/^\/+/, '');
    let url = `${normalizedBase}/${normalizedPath}`;

    if (baseQuery) {
      url += `?${baseQuery}`;
    }

    return url;
  }

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const headerMap: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headerMap['Authorization'] = `Bearer ${token}`;
    }

    return new HttpHeaders(headerMap);
  }

  getAll(filters: ServiceOrderLogFilters = {}): Observable<PagedServiceOrderLogs> {
    return this.http.get<any>(this.buildUrl(this.resourcePath), {
      headers: this.getHeaders(),
      params: this.buildParams(filters)
    }).pipe(
      map(response => this.mapPagedResponse(response, filters)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao listar logs da ordem de serviço');
        return throwError(() => err);
      })
    );
  }

  getByServiceOrderId(serviceOrderId: number, filters: Omit<ServiceOrderLogFilters, 'serviceOrderId'> = {}): Observable<PagedServiceOrderLogs> {
    return this.getAll({ ...filters, serviceOrderId });
  }

  getById(id: number): Observable<ServiceOrderLog> {
    return this.http.get<any>(this.buildUrl(`${this.resourcePath}/${id}`), {
      headers: this.getHeaders()
    }).pipe(
      map(response => this.mapFromDto(this.extractItem(response))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar log da ordem de serviço');
        return throwError(() => err);
      })
    );
  }

  private buildParams(filters: ServiceOrderLogFilters): HttpParams {
    let params = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return params;
  }

  private extractArray(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.Items)) return response.Items;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.items)) return response.data.items;
    if (Array.isArray(response?.data?.Items)) return response.data.Items;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.logs)) return response.logs;
    if (Array.isArray(response?.serviceOrderLogs)) return response.serviceOrderLogs;
    if (Array.isArray(response?.ServiceOrderLogs)) return response.ServiceOrderLogs;
    if (Array.isArray(response?.data?.logs)) return response.data.logs;
    if (Array.isArray(response?.data?.serviceOrderLogs)) return response.data.serviceOrderLogs;
    if (Array.isArray(response?.data?.ServiceOrderLogs)) return response.data.ServiceOrderLogs;
    return [];
  }

  private extractItem(response: any): any {
    return response?.data?.data
      ?? response?.data
      ?? response?.item
      ?? response?.log
      ?? response?.serviceOrderLog
      ?? response;
  }

  private mapPagedResponse(response: any, filters: ServiceOrderLogFilters): PagedServiceOrderLogs {
    const pagination = response?.data?.pagination ?? response?.data?.Pagination;
    const items = this.extractArray(response).map(item => this.mapFromDto(item));
    const pageNumber = this.toNumber(
      response?.pageNumber
      ?? response?.PageNumber
      ?? response?.currentPage
      ?? response?.CurrentPage
      ?? response?.data?.pageNumber
      ?? response?.data?.PageNumber
      ?? pagination?.pageNumber
      ?? pagination?.PageNumber
      ?? filters.pageNumber
      ?? 1
    ) ?? 1;
    const pageSize = this.toNumber(
      response?.pageSize
      ?? response?.PageSize
      ?? response?.data?.pageSize
      ?? response?.data?.PageSize
      ?? pagination?.pageSize
      ?? pagination?.PageSize
      ?? filters.pageSize
      ?? items.length
      ?? 0
    ) ?? 0;
    const totalCount = this.toNumber(
      response?.totalCount
      ?? response?.TotalCount
      ?? response?.count
      ?? response?.Count
      ?? response?.data?.totalCount
      ?? response?.data?.TotalCount
      ?? pagination?.totalCount
      ?? pagination?.TotalCount
      ?? pagination?.totalItems
      ?? pagination?.TotalItems
      ?? items.length
    ) ?? items.length;
    const totalPages = this.toNumber(
      response?.totalPages
      ?? response?.TotalPages
      ?? response?.data?.totalPages
      ?? response?.data?.TotalPages
      ?? pagination?.totalPages
      ?? pagination?.TotalPages
      ?? (pageSize > 0 ? Math.ceil(totalCount / pageSize) : 1)
    ) ?? 1;

    return {
      items,
      pageNumber,
      pageSize,
      totalCount,
      totalPages,
      hasPreviousPage: Boolean(
        response?.hasPreviousPage
        ?? response?.HasPreviousPage
        ?? response?.data?.hasPreviousPage
        ?? pageNumber > 1
      ),
      hasNextPage: Boolean(
        response?.hasNextPage
        ?? response?.HasNextPage
        ?? response?.data?.hasNextPage
        ?? pageNumber < totalPages
      )
    };
  }

  private mapFromDto(dto: any): ServiceOrderLog {
    return {
      id: this.toNumber(dto?.Id ?? dto?.id) ?? 0,
      serviceOrderId: this.toNumber(dto?.ServiceOrderId ?? dto?.serviceOrderId),
      department: dto?.Department ?? dto?.department ?? dto?.ActualDepartment ?? dto?.actualDepartment ?? null,
      userId: this.toNumber(dto?.UserId ?? dto?.userId ?? dto?.UsuarioId ?? dto?.usuarioId),
      userName: dto?.UserName ?? dto?.userName ?? dto?.UsuarioNome ?? dto?.usuarioNome ?? dto?.User?.Name ?? dto?.user?.name ?? null,
      action: dto?.Action ?? dto?.action ?? dto?.Acao ?? dto?.acao ?? null,
      description: dto?.Description ?? dto?.description ?? dto?.Mensagem ?? dto?.mensagem ?? null,
      details: dto?.Details ?? dto?.details ?? dto?.Detalhes ?? dto?.detalhes ?? null,
      oldStatus: dto?.OldStatus ?? dto?.oldStatus ?? dto?.StatusAnterior ?? dto?.statusAnterior ?? null,
      newStatus: dto?.NewStatus ?? dto?.newStatus ?? dto?.StatusNovo ?? dto?.statusNovo ?? null,
      createdAt: dto?.CreatedAt ?? dto?.createdAt ?? dto?.DataCriacao ?? dto?.dataCriacao ?? dto?.DataHora ?? dto?.dataHora ?? dto?.DateHour ?? dto?.dateHour ?? null,
      updatedAt: dto?.UpdatedAt ?? dto?.updatedAt ?? null,
      raw: dto
    };
  }

  private toNumber(value: any): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}